// Day / Night / System. System is the default and is resolved purely in CSS.
export type ThemePreference = 'system' | 'day' | 'night';
const KEY = 'sidetone.theme';
const listeners = new Set<() => void>();
// The choice still applies to this page when storage is unavailable.
let memory: ThemePreference | null = null;

export function getThemePreference(): ThemePreference {
  try {
    const value = window.localStorage.getItem(KEY);
    return value === 'day' || value === 'night' ? value : 'system';
  } catch {
    return memory ?? 'system';
  }
}

export function applyTheme(pref: ThemePreference): void {
  document.documentElement.dataset.theme = pref;
}

export function setThemePreference(pref: ThemePreference): void {
  memory = pref;
  try {
    if (pref === 'system') window.localStorage.removeItem(KEY);
    else window.localStorage.setItem(KEY, pref);
  } catch {
    // Storage may be unavailable; the choice still applies to this page.
  }
  applyTheme(pref);
  for (const listener of listeners) listener();
}

/** Settings and the command palette both change the theme; each re-reads it through this. */
export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** The theme on screen: the preference, or the operating system's when the preference is System. */
export function effectiveTheme(pref: ThemePreference = getThemePreference()): 'day' | 'night' {
  if (pref !== 'system') return pref;
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'night' : 'day';
}
